import { SwitchCamera, X, Zap } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from './Button';

export interface CameraCaptureViewLabels {
  /** t('ec2.camera.shutter') — a11y label for the shutter button. */
  shutter: string;
  /** t('ec2.camera.usePhoto') — primary confirm. */
  usePhoto: string;
  /** t('ec2.camera.retake') — ghost. */
  retake: string;
  /** t('ec2.camera.flip') — a11y label. */
  flip?: string;
  /** t('ec2.scanner.torch') — a11y label. */
  torch?: string;
  /** t('ec2.camera.close') — a11y label of the top-left exit. */
  close?: string;
}

export interface CameraCaptureViewProps {
  /** capture = live camera + shutter; preview = shot + confirm/retake. */
  mode: 'capture' | 'preview';
  labels: CameraCaptureViewLabels;
  /** Full-bleed media slot: `<video>` while capturing, `<img>` in preview. */
  media: ReactNode;
  onShutter?: () => void;
  onConfirm?: () => void;
  onRetake?: () => void;
  /** Rendered flanking the shutter when provided. */
  onFlip?: () => void;
  onToggleTorch?: () => void;
  /** Thin 3:4 frame guide over the live camera (photo quests). */
  frameGuide?: boolean;
  /** Hardware zoom chips (capture mode): levels the camera supports, e.g. [1, 2, 5]. */
  zoomLevels?: number[];
  /** Currently applied zoom (matches one of zoomLevels). */
  zoom?: number;
  onZoom?: (level: number) => void;
  /** Extra slot on the scrim (e.g. the photo-wall opt-in toggle in preview). */
  footerExtra?: ReactNode;
  /**
   * Leave the takeover without a photo. Rendered top-left (like the scanner's
   * close) in both modes — a full-screen camera must never be a dead end.
   */
  onClose?: () => void;
  className?: string;
}

/**
 * Photo-capture chrome only (brief §6 "Photo-quest capture flow", beats 2–3) —
 * no camera logic. Full-screen media slot under a bottom scrim; capture mode
 * shows a 72px shutter (white ring, --primary core) flanked by flip/torch;
 * preview mode shows `Retake` (ghost, left) / `Use this photo` (primary, right).
 */
export function CameraCaptureView({
  mode,
  labels,
  media,
  onShutter,
  onConfirm,
  onRetake,
  onFlip,
  onToggleTorch,
  frameGuide = false,
  zoomLevels,
  zoom,
  onZoom,
  footerExtra,
  onClose,
  className = '',
}: CameraCaptureViewProps) {
  return (
    // fixed inset-0: a full-bleed takeover must ignore the body's PWA
    // safe-area padding-top, which shifts in-flow h-dvh containers down.
    <div data-testid="ec-camera-view" data-mode={mode} className={`fixed inset-0 overflow-hidden bg-ink ${className}`}>
      <div className="absolute inset-0 [&>*]:h-full [&>*]:w-full [&>*]:object-cover">{media}</div>

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label={labels.close}
          data-testid="ec-camera-close"
          className="absolute left-4 top-4 z-10 flex size-11 items-center justify-center rounded-full bg-surface-overlay/70 text-fg backdrop-blur focus-visible:outline-2 focus-visible:outline-primary"
          style={{ marginTop: 'env(safe-area-inset-top)' }}
        >
          <X size={22} aria-hidden />
        </button>
      )}

      {mode === 'capture' && frameGuide && (
        <div
          aria-hidden
          data-testid="ec-frame-guide"
          className="pointer-events-none absolute left-1/2 top-1/2 aspect-[3/4] w-[78%] -translate-x-1/2 -translate-y-1/2 rounded-(--radius-card) border border-white/70"
        />
      )}

      <div
        className="ec-safe-bottom absolute inset-x-0 bottom-0 flex flex-col items-center gap-4 pb-8 pt-24"
        style={{ background: 'var(--gradient-scanner-scrim)' }}
      >
        {footerExtra}
        {mode === 'capture' && zoomLevels && zoomLevels.length > 1 && onZoom && (
          <div className="flex items-center gap-2" role="group" data-testid="ec-zoom">
            {zoomLevels.map((level) => {
              const active = zoom === level;
              return (
                <button
                  key={level}
                  type="button"
                  onClick={() => onZoom(level)}
                  aria-pressed={active}
                  data-testid={`ec-zoom-${level}`}
                  className={
                    `flex h-9 min-w-11 items-center justify-center rounded-full px-2 font-display text-[13px] font-[650] tabular-nums backdrop-blur ` +
                    `focus-visible:outline-2 focus-visible:outline-primary ` +
                    (active ? 'bg-white text-ink' : 'bg-surface-overlay/70 text-white')
                  }
                >
                  {level}×
                </button>
              );
            })}
          </div>
        )}
        {mode === 'capture' ? (
          <div className="flex w-full items-center justify-center gap-10">
            {onFlip && (
              <button
                type="button"
                onClick={onFlip}
                aria-label={labels.flip}
                data-testid="ec-flip"
                className="flex size-12 items-center justify-center rounded-full bg-surface-overlay/70 text-fg backdrop-blur focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                <SwitchCamera size={22} aria-hidden />
              </button>
            )}
            <button
              type="button"
              onClick={onShutter}
              aria-label={labels.shutter}
              data-testid="ec-shutter"
              className="flex size-[72px] items-center justify-center rounded-full border-4 border-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              <span aria-hidden className="block size-14 rounded-full bg-primary" />
            </button>
            {onToggleTorch && (
              <button
                type="button"
                onClick={onToggleTorch}
                aria-label={labels.torch}
                data-testid="ec-torch"
                className="flex size-12 items-center justify-center rounded-full bg-surface-overlay/70 text-fg backdrop-blur focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                <Zap size={22} aria-hidden />
              </button>
            )}
          </div>
        ) : (
          <div className="flex w-full max-w-md items-center justify-between gap-3 px-4">
            <Button variant="ghost" onClick={onRetake} data-testid="ec-retake" className="border-white/60 text-white">
              {labels.retake}
            </Button>
            <Button variant="primary" onClick={onConfirm} data-testid="ec-confirm">
              {labels.usePhoto}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
